import { get, set, remove, update } from './lib/gsru.js';
import { push as arrayPush, unshift as arrayUnshift, splice as arraySplice } from './lib/array.js';
import { createMarker, createChildMarker, dispatch } from './lib/marker.js';
import { pack } from './admin/pack.js';
import { unpack } from './admin/unpack.js';
import { clear } from './lib/tree/mutate.js';
import { findWhen, findAllWhen, findWhere, findAllWhere } from './lib/tree/seek.js';
import { traverse } from './lib/tree/traverse.js';
import { CREATED, DELETED, UPDATED } from './lib/TOKENS.js';
import { createChange } from './lib/change.js';
import { clone } from './utils/clone.js';

export class State {
  #state;
  #marker;
  #batchDepth = 0;
  #changeHandler;
  #accessHandler;
  #plugins = new Map();

  static create (state = {}) {
    const instance = new State(state);

    return instance;
  }

  constructor(state, marker = null, changeHandler = null, accessHandler = null) {
    this.#state = state;
    this.#changeHandler = changeHandler || this.#createChangeHandler();
    this.#accessHandler = accessHandler || this.#createAccessHandler();
    this.#marker = marker || createMarker();
    this.#batchDepth = 0;
  }

  install(...plugins) {
    const initializeFunctions = new Map();

    for (const plugin of plugins) {
      if (this.#plugins.has(plugin.name)) {
        console.warn(`Plugin "${plugin.name}" already installed`);
        continue;
      }

      if (!plugin.install) {
        console.warn(`Plugin "${plugin.name}" does not have an install function`);
        continue;
      }

      const pluginInstance = plugin.install(this);
      this.#plugins.set(plugin.name, pluginInstance);

      if (pluginInstance.initialize) {
        initializeFunctions.set(plugin.name, () => pluginInstance.initialize(this, pluginInstance.options));
      }

      if (pluginInstance.actions) {
        const actions = pluginInstance.actions(this, pluginInstance.options);
        Object.defineProperty(this, plugin.name, {
          value: actions,
          writable: false,
          enumerable: true,
          configurable: false
        });
      }
    }

    initializeFunctions.forEach(initialize => initialize());
    return this;
  }

  get batching() {
    return this.#batchDepth > 0;
  }

  // Core State Accessors
  get(...args) {
    return get(this.#state, createChildMarker(this.#marker, args), this.#accessHandler);
  }

  clone(...args) {
    const data = get(this.#state, createChildMarker(this.#marker, args), this.#accessHandler);
    if (typeof data === 'object' && data !== null) {
      try {
        return clone(data);
      } catch (_) {
        console.warn(`Failed to clone data: ${data}`);
        return data;
      }
    }
    return data;
  }

  has(...args) {
    const marker = createChildMarker(this.#marker, args);
    return dispatch(marker, {
      global: () => undefined,
      path: (marker) => get(this.#state, marker, this.#accessHandler) !== undefined,
    });
  }

  keys(...args) {
    const marker = createChildMarker(this.#marker, args);
    return dispatch(marker, {
      global: () => Object.keys(this.#state),
      path: (marker) => Object.keys(get(this.#state, marker, this.#accessHandler) || {}),
    });
  }

  typeof(...args) {
    const marker = createChildMarker(this.#marker, args);
    const value = dispatch(marker, {
      global: () => undefined,
      path: ({ path }) => typeof get(this.#state, path, this.#accessHandler),
    });
    return value;
  }

  // Path queries (seek-based, returns paths)
  findWhen(key, matcher = 'is', value) {
    const state = get(this.#state, this.#marker, null);
    return findWhen(state, key, matcher, value);
  }

  findAllWhen(key, matcher = 'is', value) {
    const state = get(this.#state, this.#marker, null);
    return findAllWhen(state, key, matcher, value);
  }

  // Value queries (object-query, returns values)
  findWhere(...args) {
    const query = args.pop();
    const parentMarker = args.length ? createChildMarker(this.#marker, args) : this.#marker;
    const subtree = get(this.#state, parentMarker, this.#accessHandler);
    const targetPath = findWhere(subtree, query);
    if (targetPath === undefined) return undefined;
    return traverse(subtree, targetPath);
  }

  findAllWhere(...args) {
    const query = args.pop();
    const parentMarker = args.length ? createChildMarker(this.#marker, args) : this.#marker;
    const subtree = get(this.#state, parentMarker, this.#accessHandler);
    const paths = findAllWhere(subtree, query);
    return paths.map(p => traverse(subtree, p));
  }

  // Mutations
  set(...args) {
    const value = args.pop();    
    const marker = createChildMarker(this.#marker, args);
    // No sync tombstone check - let async processing handle conflicts
    return set(this.#state, marker, value, 'set', this.#changeHandler);
  }

  update(...args) {
    const fn = args.pop();
    const marker = createChildMarker(this.#marker, args);
    
    // No sync tombstone check - let async processing handle conflicts  
    return update(this.#state, marker, fn, 'update', this.#changeHandler);
  }

  apply(changeEntries) {
    for (const changeEntry of changeEntries) {
      const { path, operation, method, to, query } = changeEntry;

      let marker;
      if (query) {
        const searchPath = path.slice(0, -1);
        const parentMarker = createChildMarker(this.#marker, [searchPath]);
        const subtree = get(this.#state, parentMarker, this.#accessHandler);

        let targetPath;
        if (Array.isArray(query)) {
          const [key, matcher, testValue] = query;
          const fullPath = findWhen(subtree, key, matcher, testValue);
          targetPath = fullPath ? fullPath.slice(0, -1) : undefined;
        } else {
          targetPath = findWhere(subtree, query);
        }

        if (targetPath === undefined) continue;
        marker = createChildMarker(this.#marker, [[...searchPath, ...targetPath]]);
      } else {
        marker = createChildMarker(this.#marker, path);
      }

      switch (operation) {
        case CREATED:
        case UPDATED:
          if (method === 'push') {
            arrayPush(this.#state, marker, to, 'apply', this.#changeHandler);
          } else if (method === 'unshift') {
            arrayUnshift(this.#state, marker, to, 'apply', this.#changeHandler);
          } else if (method === 'splice') {
            arraySplice(this.#state, marker, to, 'apply', this.#changeHandler);
          } else if (method === 'removeMatch') {
            const arr = get(this.#state, marker, this.#accessHandler);
            if (Array.isArray(arr)) {
              const serialized = JSON.stringify(to);
              const idx = arr.findIndex(item => JSON.stringify(item) === serialized);
              if (idx !== -1) arr.splice(idx, 1);
            }
          } else {
            set(this.#state, marker, to, 'apply', this.#changeHandler);
          }
          break;
        case DELETED:
          remove(this.#state, marker, 'apply', this.#changeHandler);
          break;
      }
    }
  }

  load(value) {
    set(this.#state, this.#marker, value, 'load', this.#changeHandler);
    return this;
  }

  remove(...args) {
    const marker = createChildMarker(this.#marker, args);
    return remove(this.#state, marker, 'remove', this.#changeHandler);
  }

  // Array Mutations
  push(...args) {
    const value = args.pop();
    const marker = createChildMarker(this.#marker, args);
    return arrayPush(this.#state, marker, value, 'push', this.#changeHandler);
  }

  unshift(...args) {
    const value = args.pop();
    const marker = createChildMarker(this.#marker, args);
    return arrayUnshift(this.#state, marker, value, 'unshift', this.#changeHandler);
  }

  splice(path, ...spliceArgs) {
    const marker = createChildMarker(this.#marker, Array.isArray(path[0]) ? path : [path]);
    return arraySplice(this.#state, marker, spliceArgs, 'splice', this.#changeHandler);
  }

  // Object-query mutations (first match)
  #resolveByPath(path) {
    return Array.isArray(path[0]) ? path[0] : path;
  }

  setWhere(path, query, value) {
    const parentMarker = createChildMarker(this.#marker, path);
    const subtree = get(this.#state, parentMarker, this.#accessHandler);
    const targetPath = findWhere(subtree, query);
    if (targetPath === undefined) return undefined;
    const fullPath = [...this.#resolveByPath(path), ...targetPath];
    const marker = createChildMarker(this.#marker, [fullPath]);
    return set(this.#state, marker, value, 'setWhere', (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query));
  }

  updateWhere(path, query, fn) {
    const parentMarker = createChildMarker(this.#marker, path);
    const subtree = get(this.#state, parentMarker, this.#accessHandler);
    const targetPath = findWhere(subtree, query);
    if (targetPath === undefined) return undefined;
    const fullPath = [...this.#resolveByPath(path), ...targetPath];
    const marker = createChildMarker(this.#marker, [fullPath]);
    return update(this.#state, marker, fn, 'updateWhere', (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query));
  }

  removeWhere(path, query) {
    const parentMarker = createChildMarker(this.#marker, path);
    const subtree = get(this.#state, parentMarker, this.#accessHandler);
    const targetPath = findWhere(subtree, query);
    if (targetPath === undefined) return undefined;
    const fullPath = [...this.#resolveByPath(path), ...targetPath];
    const marker = createChildMarker(this.#marker, [fullPath]);
    return remove(this.#state, marker, 'removeWhere', (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query));
  }

  // Object-query mutations (all matches)
  setAllWhere(path, query, value) {
    return this.batch(() => {
      const parentMarker = createChildMarker(this.#marker, path);
      const subtree = get(this.#state, parentMarker, this.#accessHandler);
      const paths = findAllWhere(subtree, query);
      const base = this.#resolveByPath(path);
      const cb = (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query);
      for (const targetPath of paths) {
        const marker = createChildMarker(this.#marker, [[...base, ...targetPath]]);
        set(this.#state, marker, value, 'setAllWhere', cb);
      }
    });
  }

  updateAllWhere(path, query, fn) {
    return this.batch(() => {
      const parentMarker = createChildMarker(this.#marker, path);
      const subtree = get(this.#state, parentMarker, this.#accessHandler);
      const paths = findAllWhere(subtree, query);
      const base = this.#resolveByPath(path);
      const cb = (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query);
      for (const targetPath of paths) {
        const marker = createChildMarker(this.#marker, [[...base, ...targetPath]]);
        update(this.#state, marker, fn, 'updateAllWhere', cb);
      }
    });
  }

  removeAllWhere(path, query) {
    return this.batch(() => {
      const parentMarker = createChildMarker(this.#marker, path);
      const subtree = get(this.#state, parentMarker, this.#accessHandler);
      const paths = findAllWhere(subtree, query);
      const base = this.#resolveByPath(path);
      const cb = (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query);
      const results = [];
      for (let i = paths.length - 1; i >= 0; i--) {
        const marker = createChildMarker(this.#marker, [[...base, ...paths[i]]]);
        results.push(remove(this.#state, marker, 'removeAllWhere', cb));
      }
      return results;
    });
  }

  // Seek-based mutations (first match)
  setWhen(key, matcher, testValue, newValue) {
    const query = [key, matcher, testValue];
    const targetPath = this.findWhen(key, matcher, testValue);
    if (!targetPath) return undefined;
    const parentPath = targetPath.slice(0, -1);
    const marker = createChildMarker(this.#marker, [parentPath]);
    return set(this.#state, marker, newValue, 'setWhen', (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query));
  }

  updateWhen(key, matcher, testValue, fn) {
    const query = [key, matcher, testValue];
    const targetPath = this.findWhen(key, matcher, testValue);
    if (!targetPath) return undefined;
    const parentPath = targetPath.slice(0, -1);
    const marker = createChildMarker(this.#marker, [parentPath]);
    return update(this.#state, marker, fn, 'updateWhen', (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query));
  }

  removeWhen(key, matcher, testValue) {
    const query = [key, matcher, testValue];
    const targetPath = this.findWhen(key, matcher, testValue);
    if (!targetPath) return undefined;
    const parentPath = targetPath.slice(0, -1);
    const marker = createChildMarker(this.#marker, [parentPath]);
    return remove(this.#state, marker, 'removeWhen', (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query));
  }

  // Seek-based mutations (all matches)
  setAllWhen(key, matcher, testValue, newValue) {
    return this.batch(() => {
      const query = [key, matcher, testValue];
      const cb = (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query);
      const paths = this.findAllWhen(key, matcher, testValue);
      for (const targetPath of paths) {
        const parentPath = targetPath.slice(0, -1);
        const marker = createChildMarker(this.#marker, [parentPath]);
        set(this.#state, marker, newValue, 'setAllWhen', cb);
      }
    });
  }

  updateAllWhen(key, matcher, testValue, fn) {
    return this.batch(() => {
      const query = [key, matcher, testValue];
      const cb = (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query);
      const paths = this.findAllWhen(key, matcher, testValue);
      for (const targetPath of paths) {
        const parentPath = targetPath.slice(0, -1);
        const marker = createChildMarker(this.#marker, [parentPath]);
        update(this.#state, marker, fn, 'updateAllWhen', cb);
      }
    });
  }

  removeAllWhen(key, matcher, testValue) {
    return this.batch(() => {
      const query = [key, matcher, testValue];
      const cb = (marker, method, to, from) => this.#changeHandler(marker, method, to, from, query);
      const paths = this.findAllWhen(key, matcher, testValue);
      const results = [];
      for (let i = paths.length - 1; i >= 0; i--) {
        const parentPath = paths[i].slice(0, -1);
        const marker = createChildMarker(this.#marker, [parentPath]);
        results.push(remove(this.#state, marker, 'removeAllWhen', cb));
      }
      return results;
    });
  }

  // Lifecycle / Admin
  async export(...args) {
    try {
      const marker = createChildMarker(this.#marker, args);
      const promises = dispatch(marker, {
        global: async () => await pack(this.#state),
        path: async (path) => await pack(get(this.#state, path, this.#accessHandler)),
      });
      if (!Array.isArray(promises)) {
        return await promises;
      }
      return await Promise.all(promises);
    } catch (err) {
      console.trace(`Failed to export state: ${err.message}`);
      throw err
    }
  }

  async import(exportedData) {
    try {
      const { data } = await unpack(exportedData);
      this.load(data);
      return this;
    } catch (err) {
      console.trace(`Failed to import state: ${err.message}`);
      throw err
    }
  }

  reset() {
    clear(this.#state);
    for (const plugin of this.#plugins.values()) {
      if (plugin.reset) {
        plugin.reset();
      }
    }
    this.#changeHandler = this.#createChangeHandler();
    this.#marker = createMarker([], this);
    this.#batchDepth = 0;
  }


  // Reactivity Utilities
  ref(...path) {
    const marker = createChildMarker(this.#marker, path);
    return new State(this.#state, marker, this.#changeHandler, this.#accessHandler);
  }

  batch(fn) {
    this.#batchDepth++;
    this.#notifyPlugins('onBatchStart', this.batching);
    if (fn) {
      fn(this);
      return this.endBatch();
    }
    return () => this.endBatch();
  }

  endBatch() {
    this.#batchDepth--;
    if (this.#batchDepth === 0) {
      this.#notifyPlugins('onBatchEnd');
    }
    return this;
  }

  // Private change handler
  #createChangeHandler() {
    return (marker, method, to = undefined, from = undefined, query) => {
      this.#notifyPlugins('onStateChange', marker, createChange(marker, method, to, from, query), this.batching);
    }
  }

  #createAccessHandler() {
    return (marker) => this.#notifyPlugins('onStateAccess', marker);
  }

  #notifyPlugins(event, ...args) {
    for (const plugin of this.#plugins.values()) {
      if (typeof plugin[event] === 'function') {
        plugin[event](...args);
      }
    }
  }
}

export const createState = (...args) => State.create(...args);
export const isState = (target) => target instanceof State;